import { UploadCloud } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CipScreenShell, CipSpinner } from '../../components/smart/cip';
import { SmartShell } from '../../components/smart/SmartShell';
import { Button } from '../../components/smart/ui/Button';
import { SMART_ROUTES } from '../../constants';
import {
  smartMockCipAddress,
  smartMockCipScanData,
} from '../../gallery/smartMock';

/**
 * Gallery-static CIP step shells. No Kentucky ticket, no AWS liveness, no
 * network — CTAs only chain between inspectable screens.
 */

const CipWizardShell = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('smart');
  return (
    <SmartShell
      title={t('wizard.headline.kyc.title')}
      subtitle={t('wizard.headline.kyc.subtitle')}
    >
      {children}
    </SmartShell>
  );
};

const REVIEW_FIELDS: Array<{ key: string; labelKey: string }> = [
  { key: 'FullName', labelKey: 'fullName' },
  { key: 'DocumentType', labelKey: 'documentType' },
  { key: 'IDNumber', labelKey: 'idNumber' },
  { key: 'DoB', labelKey: 'dob' },
  { key: 'ExpDate', labelKey: 'expDate' },
  { key: 'Country', labelKey: 'country' },
  { key: 'Address', labelKey: 'address' },
];

const ADDRESS_FIELDS = [
  { key: 'street1', required: true },
  { key: 'street2', required: false },
  { key: 'city', required: true },
  { key: 'state', required: true },
  { key: 'postalCode', required: true },
  { key: 'countryCode', required: true },
] as const;

type AddressKey = (typeof ADDRESS_FIELDS)[number]['key'];

const SCAN_ID_PROGRESS_KEYS = [
  'loading.document.loading.photo',
  'loading.document.checking.image',
  'loading.document.detecting.document',
  'loading.document.aligning',
  'loading.document.reducing',
  'loading.document.reading.text',
] as const;

export const CipClaimingPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();

  useEffect(() => {
    const id = window.setTimeout(() => {
      navigate(SMART_ROUTES.CIP_SCAN_ID);
    }, 1800);
    return () => window.clearTimeout(id);
  }, [navigate]);

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.claiming.title')}
        description={t('wizard.cip.claiming.description')}
      >
        <div className="mt-8 flex justify-center">
          <CipSpinner className="h-8 w-8" />
        </div>
      </CipScreenShell>
    </CipWizardShell>
  );
};

export const CipScanIdPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const pickFile = (selected: File | null) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(selected ? URL.createObjectURL(selected) : null);
  };

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.scanId.title')}
        description={t('wizard.cip.scanId.description')}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
        />

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="border-border bg-bg mt-6 flex aspect-[3/2] w-full items-center justify-center overflow-hidden rounded-xl border border-dashed transition-colors hover:border-accent"
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={t('wizard.cip.scanId.previewAlt')}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-fg-muted flex flex-col items-center gap-3 px-6 text-center text-sm">
              <UploadCloud className="h-9 w-9" aria-hidden="true" />
              {t('wizard.cip.scanId.pickCta')}
            </span>
          )}
        </button>

        <div className="mt-6 grid grid-cols-1 gap-2">
          <Button
            fullWidth
            size="lg"
            disabled={!previewUrl}
            onClick={() => navigate(SMART_ROUTES.CIP_SCAN_ID_LOADING)}
          >
            {t('wizard.cip.scanId.submitCta')}
          </Button>
        </div>
      </CipScreenShell>
    </CipWizardShell>
  );
};

export const CipScanIdLoadingPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const rotate = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % SCAN_ID_PROGRESS_KEYS.length);
    }, 3000);
    const advance = window.setTimeout(() => {
      navigate(SMART_ROUTES.CIP_IDENTITY_REVIEW);
    }, 2200);
    return () => {
      window.clearInterval(rotate);
      window.clearTimeout(advance);
    };
  }, [navigate]);

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.scanId.title')}
        description={t('wizard.cip.scanId.description')}
      >
        <div className="mt-8 flex flex-col items-center justify-center gap-4 py-6">
          <CipSpinner className="h-8 w-8" />
          <p
            aria-live="polite"
            className="text-fg-muted min-h-[1.25rem] text-center text-sm"
          >
            {t(SCAN_ID_PROGRESS_KEYS[index])}
          </p>
        </div>
      </CipScreenShell>
    </CipWizardShell>
  );
};

export const CipIdentityReviewPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const data = smartMockCipScanData;
  const rows = REVIEW_FIELDS.filter(({ key }) => data[key]);

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.identityReview.title')}
        description={t('wizard.cip.identityReview.description')}
      >
        <dl className="border-border bg-bg mt-6 divide-y divide-border rounded-xl border">
          {rows.map(({ key, labelKey }) => (
            <div
              key={key}
              className="flex items-start justify-between gap-4 px-4 py-3"
            >
              <dt className="text-fg-muted text-xs font-semibold uppercase tracking-wide">
                {t(`wizard.cip.identityReview.fields.${labelKey}`)}
              </dt>
              <dd className="text-fg text-right text-sm font-medium">
                {data[key]}
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 grid grid-cols-1 gap-2">
          <Button
            fullWidth
            size="lg"
            onClick={() => navigate(SMART_ROUTES.CIP_ADDRESS)}
          >
            {t('wizard.cip.identityReview.confirmCta')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={() => navigate(SMART_ROUTES.CIP_SCAN_ID)}
          >
            {t('wizard.cip.identityReview.rejectCta')}
          </Button>
        </div>
      </CipScreenShell>
    </CipWizardShell>
  );
};

export const CipAddressPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const [values, setValues] = useState<Record<AddressKey, string>>({
    ...smartMockCipAddress,
  });

  const requiredFilled = ADDRESS_FIELDS.filter((f) => f.required).every(
    (f) => values[f.key].trim().length > 0,
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!requiredFilled) return;
    navigate(SMART_ROUTES.CIP_LIVENESS);
  };

  const renderField = (f: (typeof ADDRESS_FIELDS)[number]) => (
    <label key={f.key} className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
        {t(`wizard.cip.address.fields.${f.key}`)}
        {!f.required && (
          <span className="text-fg-muted ml-1 normal-case">
            ({t('wizard.cip.address.optional')})
          </span>
        )}
      </span>
      <input
        name={f.key}
        value={values[f.key]}
        onChange={(e) =>
          setValues((v) => ({ ...v, [f.key]: e.target.value }))
        }
        className="border-border bg-bg focus:ring-accent w-full rounded-xl border px-4 py-3 text-sm outline-none focus:ring-2"
      />
    </label>
  );

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.address.title')}
        description={t('wizard.cip.address.description')}
      >
        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3">
          {renderField(ADDRESS_FIELDS[0])}
          {renderField(ADDRESS_FIELDS[1])}
          {renderField(ADDRESS_FIELDS[2])}
          {renderField(ADDRESS_FIELDS[3])}
          <div className="flex gap-3">
            {renderField(ADDRESS_FIELDS[4])}
            {renderField(ADDRESS_FIELDS[5])}
          </div>
          <Button type="submit" disabled={!requiredFilled} fullWidth size="lg">
            {t('wizard.cip.address.submitCta')}
          </Button>
        </form>
      </CipScreenShell>
    </CipWizardShell>
  );
};

export const CipLivenessLoadingPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();

  useEffect(() => {
    const id = window.setTimeout(() => {
      navigate(SMART_ROUTES.CIP_LIVENESS_CAPTURE);
    }, 1800);
    return () => window.clearTimeout(id);
  }, [navigate]);

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.liveness.title')}
        description={t('wizard.cip.liveness.loadingDescription')}
      >
        <div className="mt-8 flex justify-center">
          <CipSpinner className="h-8 w-8" />
        </div>
      </CipScreenShell>
    </CipWizardShell>
  );
};

/**
 * Capture layout stub — live flow mounts AWS FaceLivenessDetector here.
 * Gallery shows the tall portrait stage + oval chrome without the SDK.
 */
export const CipLivenessCapturePage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();

  return (
    <SmartShell hideHeader>
      <div className="cip-liveness-capture">
        <p className="text-fg-muted text-[10px] font-semibold uppercase tracking-[0.2em]">
          {t('wizard.cip.eyebrow')}
        </p>
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t('wizard.cip.liveness.title')}
          </h2>
          <p className="text-fg-muted mt-2 text-sm">
            {t('wizard.cip.liveness.captureDescription')}
          </p>
        </div>
        <div className="cip-liveness-stage flex items-center justify-center">
          <div
            aria-hidden
            className="border-border/60 h-[58%] w-[52%] rounded-[50%] border-2 border-dashed"
          />
        </div>
        <div className="grid grid-cols-1 gap-2">
          <Button
            fullWidth
            size="lg"
            onClick={() => navigate(SMART_ROUTES.CIP_COMPLETION)}
          >
            {t('wizard.cip.scanId.submitCta')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={() => navigate(SMART_ROUTES.CIP_LIVENESS_RETRY)}
          >
            {t('wizard.cip.liveness.retryCta')}
          </Button>
        </div>
      </div>
    </SmartShell>
  );
};

export const CipLivenessRetryPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.liveness.retryTitle')}
        description={t('wizard.cip.liveness.retryDescription')}
      >
        <Button
          fullWidth
          size="lg"
          className="mt-6"
          onClick={() => navigate(SMART_ROUTES.CIP_LIVENESS_CAPTURE)}
        >
          {t('wizard.cip.liveness.retryCta')}
        </Button>
      </CipScreenShell>
    </CipWizardShell>
  );
};

const PROCESSING_STEP_INTERVAL_MS = 4500;

export const CipCompletionProcessingPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const steps = t('wizard.cip.completion.processingSteps', {
    returnObjects: true,
  });
  const messages = Array.isArray(steps)
    ? (steps as string[])
    : [t('wizard.cip.completion.processingDescription')];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (messages.length <= 1) return;
    const id = window.setInterval(() => {
      setIndex((prev) => Math.min(prev + 1, messages.length - 1));
    }, PROCESSING_STEP_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [messages.length]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      navigate(SMART_ROUTES.CIP_COMPLETION_SUCCESS);
    }, 2400);
    return () => window.clearTimeout(id);
  }, [navigate]);

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.completion.processingTitle')}
      >
        <div className="mt-8 flex flex-col items-center gap-4">
          <CipSpinner className="h-8 w-8" />
          <p
            key={index}
            aria-live="polite"
            className="cip-step-message text-fg-muted min-h-[2.5rem] text-center text-sm"
          >
            {messages[index]}
          </p>
        </div>
      </CipScreenShell>
    </CipWizardShell>
  );
};

export const CipCompletionSuccessPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();

  return (
    <CipWizardShell>
      <CipScreenShell
        label={t('wizard.cip.eyebrow')}
        title={t('wizard.cip.completion.successTitle')}
        description={t('wizard.cip.completion.successDescription')}
      >
        <Button
          fullWidth
          size="lg"
          className="mt-6"
          onClick={() => navigate(SMART_ROUTES.COMMITTING)}
        >
          {t('wizard.cip.completion.continueCta')}
        </Button>
      </CipScreenShell>
    </CipWizardShell>
  );
};
